import { merge, set } from 'lodash';

import { WorkItemStatus } from '@/apps/dispatcher/types/enums';
import { IWorkItem } from '@/apps/dispatcher/types/IWorkItem';
import {
  deduplicateWorkItems,
  isNewerWorkItemVersion,
  WorkItemId,
  workItemIdKey,
  WorkItemListScope,
  workItemMatchesScope,
  WorkItemReconciliationState,
} from '@/apps/dispatcher/redux/workItemScope';
import {
  getDeepDifferences,
  normalizeChangesWithNestedObjects,
} from '@/utils/utils';
import { devLog, devWarn } from '@/utils/logging';
import { isDevOrLocalEnvironment } from '@/utils/url';

export {
  createWorkItemListScope,
  isCompleteWorkItem,
  signalMatchesScope,
  workItemFromCreateSignal,
  workItemMatchesScope,
} from '@/apps/dispatcher/redux/workItemScope';
export type {
  ActiveWorkItemFetch,
  ActiveWorkItemMutation,
  WorkItemId,
  WorkItemListScope,
  WorkItemReconciliationState,
} from '@/apps/dispatcher/redux/workItemScope';

export const mergeWorkItemSnapshot = (
  current: IWorkItem[],
  snapshot: IWorkItem[],
  scope: WorkItemListScope,
  touchedIds: WorkItemId[]
): IWorkItem[] => {
  const touched = new Set(touchedIds.map(workItemIdKey));
  const fresh = deduplicateWorkItems(snapshot).filter(
    (workItem) =>
      workItem.id !== undefined &&
      workItemMatchesScope(workItem, scope) &&
      !touched.has(workItemIdKey(workItem.id))
  );
  const freshIds = new Set(
    fresh.map((workItem) => workItemIdKey(workItem.id as WorkItemId))
  );
  const preserved = deduplicateWorkItems(
    current.filter((workItem) => {
      if (workItem.id === undefined) {
        return false;
      }

      const key = workItemIdKey(workItem.id);
      if (freshIds.has(key)) {
        return false;
      }

      if (workItem.status !== scope.status) {
        return true;
      }

      return touched.has(key) && workItemMatchesScope(workItem, scope);
    })
  );

  return [...preserved, ...fresh];
};

export const touchActiveFetches = (
  state: WorkItemReconciliationState,
  id: WorkItemId
): void => {
  Object.values(state.activeFetches).forEach((fetch) => {
    if (
      fetch &&
      !fetch.touchedIds.some(
        (touchedId) => workItemIdKey(touchedId) === workItemIdKey(id)
      )
    ) {
      fetch.touchedIds.push(id);
    }
  });
};

const flattenChanges = (
  value: unknown,
  prefix = '',
  result: Record<string, unknown> = {}
): Record<string, unknown> => {
  if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
    Object.entries(value).forEach(([key, nestedValue]) => {
      flattenChanges(nestedValue, prefix ? `${prefix}.${key}` : key, result);
    });
  } else if (prefix) {
    result[prefix] = value;
  }

  return result;
};

const mergeIntoActiveMutation = (
  state: WorkItemReconciliationState,
  id: WorkItemId,
  changes: Partial<IWorkItem>
): void => {
  const mutation = state.activeMutations[workItemIdKey(id)];
  const lastModified = changes.lastModified;
  if (!mutation || typeof lastModified !== 'string') {
    return;
  }

  Object.entries(flattenChanges(changes)).forEach(([path, value]) => {
    if (path === 'lastModified') {
      return;
    }

    const previous = mutation.changesAfterStart[path];
    if (
      !previous ||
      isNewerWorkItemVersion(lastModified, previous.lastModified)
    ) {
      mutation.changesAfterStart[path] = { lastModified, value };
    }
  });
};

export const mergeWorkItemSignal = (
  state: WorkItemReconciliationState,
  id: WorkItemId,
  changes: Partial<IWorkItem>,
  source: 'API' | 'RTP'
): void => {
  const normalizedChanges = normalizeChangesWithNestedObjects(changes);
  touchActiveFetches(state, id);
  mergeIntoActiveMutation(state, id, normalizedChanges);

  const index = state.workItems.findIndex(
    (item) =>
      item.id !== undefined && workItemIdKey(item.id) === workItemIdKey(id)
  );
  const existingItem = state.workItems[index];

  if (isDevOrLocalEnvironment && existingItem) {
    const differences = getDeepDifferences(existingItem, {
      ...existingItem,
      ...normalizedChanges,
    });
    devLog(
      differences
        ? `WorkItem ${source} Update:`
        : `WorkItem ${source} Update - No changes detected for work item:`,
      id,
      ...(differences ? [differences] : [])
    );
  }

  // Before the list lookup: an item opened from the queued-history search is
  // not in the live list, and its open wizard still has to see the change.
  if (state.activeWorkItemId === id && state.activeWorkItem) {
    state.activeWorkItem = merge(
      {},
      state.activeWorkItem,
      normalizedChanges
    ) as IWorkItem;
  }

  if (index === -1 || !existingItem) {
    devWarn(`Work item not found for ${source} update:`, id);
    return;
  }

  state.workItems[index] = merge(
    {},
    existingItem,
    normalizedChanges
  ) as IWorkItem;
};

/**
 * True when an API update is the echo of a PTA annulment: its raw `changes`
 * (EF property names, connect-bus-api
 * src/ConnectBus.Infrastructure/Streaming/Helpers/SignalRUpdateMapper.cs:9-11
 * and src/ConnectBus.Infrastructure/Persistence/ConnectBusDbContext.cs:397-408)
 * set `PtaReportAnnulledAt`, which the annul broadcasts
 * (src/ConnectBus.Application/Features/WorkItems/Commands/AnnulPtaReport/AnnulWorkItemPtaReportCommandHandler.cs:45-46).
 * A null value is the annulment being cleared by a new report, an ordinary
 * update.
 */
export const isPtaAnnulEcho = (data: unknown): boolean => {
  const changes = (data as { changes?: unknown } | null)?.changes;
  if (typeof changes !== 'object' || changes === null) {
    return false;
  }
  const annulledAt = (changes as Record<string, unknown>).PtaReportAnnulledAt;
  return annulledAt !== null && annulledAt !== undefined;
};

export const reconcileCompleteWorkItem = (
  state: WorkItemReconciliationState,
  workItem: IWorkItem & { id: WorkItemId }
): void => {
  const index = state.workItems.findIndex(
    (item) =>
      item.id !== undefined &&
      workItemIdKey(item.id) === workItemIdKey(workItem.id)
  );

  if (index !== -1) {
    touchActiveFetches(state, workItem.id);
    const current = state.workItems[index];
    if (
      current &&
      isNewerWorkItemVersion(current.lastModified, workItem.lastModified)
    ) {
      return;
    }

    state.workItems[index] = workItem;
    if (
      state.activeWorkItemId !== null &&
      state.activeWorkItemId !== undefined &&
      workItemIdKey(state.activeWorkItemId) === workItemIdKey(workItem.id)
    ) {
      state.activeWorkItem = workItem;
    }
    return;
  }

  const status = workItem.status as WorkItemStatus;
  const scope =
    state.loadedScopes[status] ?? state.activeFetches[status]?.scope;
  if (
    (workItem.status === WorkItemStatus.Done ||
      workItem.status === WorkItemStatus.Archive) &&
    scope &&
    workItemMatchesScope(workItem, scope)
  ) {
    touchActiveFetches(state, workItem.id);
    state.workItems = [workItem, ...state.workItems];
  }
};

export const reconcileCreatedWorkItem = (
  state: WorkItemReconciliationState,
  workItem: IWorkItem & { id: WorkItemId }
): void => {
  const status = workItem.status as WorkItemStatus;
  const scope =
    state.loadedScopes[status] ?? state.activeFetches[status]?.scope;
  const index = state.workItems.findIndex(
    (item) =>
      item.id !== undefined &&
      workItemIdKey(item.id) === workItemIdKey(workItem.id)
  );

  if (index !== -1) {
    reconcileCompleteWorkItem(state, workItem);
  } else if (scope && workItemMatchesScope(workItem, scope)) {
    touchActiveFetches(state, workItem.id);
    state.workItems = [workItem, ...state.workItems];
  }
};

export const completeTrackedWorkItemMutation = (
  state: WorkItemReconciliationState,
  requestId: number,
  workItem: IWorkItem & { id: WorkItemId }
): void => {
  const key = workItemIdKey(workItem.id);
  const mutation = state.activeMutations[key];
  if (!mutation || mutation.requestId !== requestId) {
    return;
  }

  const laterChanges: Partial<IWorkItem> = {};
  let newestVersion = workItem.lastModified;
  Object.entries(mutation.changesAfterStart).forEach(
    ([path, { lastModified, value }]) => {
      if (isNewerWorkItemVersion(lastModified, workItem.lastModified)) {
        set(laterChanges, path, value);
        if (isNewerWorkItemVersion(lastModified, newestVersion)) {
          newestVersion = lastModified;
        }
      }
    }
  );
  if (newestVersion !== workItem.lastModified) {
    laterChanges.lastModified = newestVersion;
  }

  const reconciled = merge({}, workItem, laterChanges) as IWorkItem & {
    id: WorkItemId;
  };
  delete state.activeMutations[key];
  reconcileCompleteWorkItem(state, reconciled);
};
